import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Star } from "@phosphor-icons/react";
import { SERVICES, TESTIMONIALS, CASE_STUDIES, INDUSTRIES } from "@/lib/content";
import useSiteContent from "@/hooks/useSiteContent";

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="mask-line">
      <motion.span
        className="mask-inner"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: [0.2, 0.7, 0.2, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Reveal({ children, delay = 0, y = 24 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.2, 0.7, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const cms = useSiteContent();
  const hero = cms.hero || {};
  const heroLines = hero.lines || ["Finance.", "Compliance.", "Growth."];
  const heroSub = hero.subheading || "Chartered Accountants helping startups, SMEs and growing businesses with accounting, taxation, audits, SOPs, internal controls and Virtual CFO services — with Big-Four rigor delivered as your dedicated finance partner.";
  const heroImg = hero.image_url || "https://images.pexels.com/photos/6918658/pexels-photo-6918658.jpeg";
  const heroChapter = hero.chapter || "Chapter 01 · The Firm";
  const heroCtaP = hero.cta_primary || "Book Free Consultation";
  const heroCtaS = hero.cta_secondary || "WhatsApp Us";
  const heroBadge = hero.founder_badge || "Pankaj Talan · CA";
  const testimonials = cms.testimonials?.length ? cms.testimonials : TESTIMONIALS;
  const caseStudies = cms.case_studies?.length ? cms.case_studies : CASE_STUDIES;
  const industries = cms.industries?.length ? cms.industries : INDUSTRIES;
  const advisoryImg = cms.about?.advisory_image_url || "https://images.pexels.com/photos/8353818/pexels-photo-8353818.jpeg";

  return (
    <>
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative bg-[var(--pta-cream)] pt-8 md:pt-16 pb-20 md:pb-32 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                className="flex items-center gap-4 mb-8"
              >
                <div className="chapter-num">{heroChapter}</div>
                <div className="h-px w-24 bg-[var(--pta-gold)]" />
              </motion.div>
              <h1 className="font-serif-display leading-[0.92] text-[var(--pta-navy)] text-[16vw] sm:text-[13vw] lg:text-[10.5vw] xl:text-[9.5rem]">
                {heroLines.map((l, i) => (
                  <MaskedLine key={i} delay={0.15 + i * 0.14}>
                    {i === heroLines.length - 1 ? <em className="not-italic text-[var(--pta-gold)] italic-alt">{l}</em> : l}
                  </MaskedLine>
                ))}
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.9 }}
                className="mt-10 max-w-xl text-[var(--pta-ink)]/75 text-base md:text-lg leading-relaxed"
              >
                {heroSub}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.05 }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <Link to="/book" data-testid="hero-book" className="btn-pta">{heroCtaP} <ArrowUpRight size={14} weight="bold" /></Link>
                <a href="https://wa.me/919560029986" target="_blank" rel="noreferrer" data-testid="hero-whatsapp" className="btn-pta-outline">{heroCtaS}</a>
              </motion.div>
            </div>

            <div className="lg:col-span-4">
              <motion.div style={{ y: imgY, scale: imgScale }} className="relative">
                <div className="clip-spotlight border hairline-navy overflow-hidden bg-[var(--pta-navy)]">
                  <img
                    src={heroImg}
                    alt="Financial advisory"
                    className="w-full h-[540px] object-cover mix-blend-normal opacity-95"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-[var(--pta-gold)] text-[var(--pta-navy)] px-5 py-4 font-mono-fine text-[10px] tracking-[0.24em] uppercase">
                  <div>Founder</div>
                  <div className="text-[var(--pta-navy)] font-serif-display text-xl mt-1 normal-case tracking-normal">{heroBadge}</div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* trust bar */}
          <Reveal delay={1.2}>
            <div className="mt-20 md:mt-32 grid grid-cols-2 md:grid-cols-4 border hairline">
              {[
                { k: "SRCC", v: "Alumni · Delhi" },
                { k: "10+ Yrs", v: "Corporate Finance" },
                { k: "10", v: "Service Verticals" },
                { k: "100%", v: "Advisory-Led" },
              ].map((s, i) => (
                <div key={i} className={`p-6 md:p-8 ${i > 0 ? "border-l hairline" : ""}`}>
                  <div className="font-serif-display text-4xl md:text-5xl text-[var(--pta-navy)]">{s.k}</div>
                  <div className="mt-2 font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-ink)]/60">{s.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <section className="marquee-row bg-[var(--pta-cream)] py-6" data-testid="section-marquee">
        <Marquee gradient={false} speed={40}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 pr-10">
              {["Compliance-first", "Advisory-led", "Multi-entity expertise", "Big-Four rigor", "Boutique attention"].map((t) => (
                <span key={t} className="font-serif-display italic text-4xl md:text-6xl text-[var(--pta-navy)] flex items-center gap-10 pr-10">
                  {t}
                  <span className="text-[var(--pta-gold)] font-sans not-italic">✦</span>
                </span>
              ))}
            </div>
          ))}
        </Marquee>
      </section>

      {/* ===== MANIFESTO CHAPTERS ===== */}
      <section className="bg-[var(--pta-cream)] py-24 md:py-32" data-testid="section-manifesto">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="chapter-num">Chapter 02</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-4 text-[var(--pta-navy)] leading-[1.02]">Our<br />manifesto.</h2>
            <p className="mt-6 text-[var(--pta-ink)]/70 max-w-sm">Five beliefs that shape how we approach every client engagement.</p>
          </div>
          <div className="lg:col-span-8 space-y-0 border-t hairline">
            {[
              "Finance should enable growth, not create complexity.",
              "Strong processes reduce business risk.",
              "Compliance should be proactive, not reactive.",
              "Decisions must be backed by accurate financial data.",
              "Every growing business deserves CFO-level guidance.",
            ].map((line, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-baseline gap-6 md:gap-10 py-8 border-b hairline group cursor-default">
                  <span className="font-mono-fine text-[11px] tracking-[0.24em] text-[var(--pta-gold)]">0{i + 1}</span>
                  <span className="font-serif-display text-2xl md:text-4xl text-[var(--pta-navy)] leading-tight group-hover:translate-x-2 transition-transform duration-500">{line}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="section-dark py-24 md:py-32" data-testid="section-services">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="chapter-num">Chapter 03 · Services</div>
              <h2 className="font-serif-display text-5xl md:text-7xl text-[var(--pta-cream)] mt-4 leading-[0.98]">Ten verticals.<br /><em className="italic text-[var(--pta-gold)]">One finance partner.</em></h2>
            </div>
            <Link to="/services" data-testid="all-services" className="btn-pta-gold self-start md:self-end">All Services <ArrowRight size={14} weight="bold" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l" style={{ borderColor: 'rgba(212,175,55,0.25)' }}>
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group p-8 md:p-10 border-r border-b relative overflow-hidden"
                style={{ borderColor: 'rgba(212,175,55,0.25)' }}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono-fine text-[11px] tracking-[0.24em] text-[var(--pta-gold)]">{s.num}</span>
                  <ArrowUpRight size={18} weight="bold" className="text-white/40 group-hover:text-[var(--pta-gold)] transition-colors" />
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--pta-cream)] leading-tight">{s.title}</h3>
                <p className="mt-4 text-white/60 text-sm leading-relaxed">{s.tagline}</p>
                {s.premium && <div className="mt-6 inline-block num-pill text-[var(--pta-gold)]">Signature</div>}
                <motion.div
                  className="absolute inset-x-0 bottom-0 h-[2px] bg-[var(--pta-gold)] origin-left"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.4 }}
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ABOUT PREVIEW ===== */}
      <section className="bg-[var(--pta-cream)] py-24 md:py-32" data-testid="section-about-preview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="clip-tab border hairline-navy overflow-hidden">
              <img src={advisoryImg} alt="Advisory meeting" className="w-full h-[560px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-10 order-1 lg:order-2">
            <div className="chapter-num">Chapter 04 · Founder</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[var(--pta-navy)] mt-4 leading-[1.02]">Big-Four rigor,<br /><em className="italic text-[var(--pta-gold)]">boutique attention.</em></h2>
            <p className="mt-6 text-[var(--pta-ink)]/75 text-base md:text-lg max-w-xl leading-relaxed">
              I am Pankaj Talan — a Chartered Accountant and SRCC graduate with experience across corporate finance, financial reporting, internal controls, audit coordination and business process consulting. I have worked with one of the world's leading investment management firms where accuracy, strong controls and high-quality reporting were non-negotiable.
            </p>
            <p className="mt-4 text-[var(--pta-ink)]/70 max-w-xl">Today I help startups, SMEs and family businesses build finance functions that go beyond compliance.</p>
            <div className="mt-8 flex gap-3">
              <Link to="/about" data-testid="about-link" className="btn-pta">Read Founder Note <ArrowRight size={14} weight="bold" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CASE STUDIES ===== */}
      <section className="bg-[var(--pta-paper)] py-24 md:py-32" data-testid="section-case-studies">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="chapter-num">Chapter 05 · Outcomes</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-4 leading-[1.02] text-[var(--pta-navy)]">Anonymized, but honest.</h2>
            </div>
            <Link data-testid="all-cases" to="/case-studies" className="btn-pta-outline self-start md:self-end">All Case Studies</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.slice(0,6).map((c, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <article className="pta-card bg-[var(--pta-cream)] border hairline p-8 h-full">
                  <div className="font-mono-fine text-[11px] tracking-[0.22em] text-[var(--pta-gold)] uppercase">Case · 0{i + 1}</div>
                  <div className="font-serif-display text-5xl md:text-6xl text-[var(--pta-navy)] mt-4">{c.number}</div>
                  <h3 className="font-serif-display text-2xl text-[var(--pta-navy)] mt-2">{c.title}</h3>
                  <p className="mt-4 text-[var(--pta-ink)]/70 text-sm leading-relaxed">{c.detail}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES ===== */}
      <section className="bg-[var(--pta-cream)] py-24 md:py-32" data-testid="section-industries">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="chapter-num">Chapter 06 · Reach</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-4 text-[var(--pta-navy)] leading-[1.02]">Industries<br />we know.</h2>
            </div>
            <p className="lg:col-span-7 text-[var(--pta-ink)]/70 text-lg self-end">From manufacturing shopfloors to SaaS boardrooms — a decade of working across sectors sharpens the advice.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l hairline">
            {industries.map((ind, i) => (
              <div key={ind.name} className="border-r border-b hairline p-6 md:p-8 group cursor-default">
                <div className="font-mono-fine text-[10px] tracking-[0.22em] text-[var(--pta-gold)]">SECTOR · {String(i + 1).padStart(2, "0")}</div>
                <h4 className="font-serif-display text-2xl text-[var(--pta-navy)] mt-3 group-hover:italic transition-all">{ind.name}</h4>
                <p className="mt-2 text-sm text-[var(--pta-ink)]/60">{ind.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section-dark py-24 md:py-32" data-testid="section-testimonials">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="chapter-num">Chapter 07 · Voices</div>
          <h2 className="font-serif-display text-5xl md:text-7xl mt-4 text-[var(--pta-cream)] leading-[0.98]">What clients say<br /><em className="italic text-[var(--pta-gold)]">when the room is quiet.</em></h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <blockquote className="border p-8 md:p-10" style={{ borderColor: 'rgba(212,175,55,0.35)' }}>
                  <div className="flex gap-1 text-[var(--pta-gold)] mb-6">{[0,1,2,3,4].map(x=><Star key={x} size={14} weight="fill" />)}</div>
                  <p className="font-serif-display text-2xl md:text-3xl leading-snug text-[var(--pta-cream)]">"{t.quote}"</p>
                  <footer className="mt-8 font-mono-fine text-[11px] tracking-[0.24em] uppercase text-white/60">— {t.author} · {t.role}</footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-[var(--pta-cream)] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="chapter-num">Chapter 08 · Begin</div>
            <h2 className="font-serif-display text-5xl md:text-7xl mt-4 text-[var(--pta-navy)] leading-[0.98]">
              Ready to make finance<br /><em className="italic text-[var(--pta-gold)]">a growth asset?</em>
            </h2>
            <p className="mt-6 text-[var(--pta-ink)]/70 max-w-xl text-lg">A free 30-minute call. No slides, no jargon. Just a look at your books, your process, and what's actually holding you back.</p>
            <div className="mt-10 flex gap-3 flex-wrap">
              <Link data-testid="cta-book" to="/book" className="btn-pta">Book Free Consultation</Link>
              <Link data-testid="cta-contact" to="/contact" className="btn-pta-outline">Or Write to Us</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border hairline-navy p-8">
              <div className="overline">Direct lines</div>
              <div className="mt-4 divide-y hairline">
                <a data-testid="cta-phone" href="tel:+919560029986" className="flex items-baseline justify-between py-4 group">
                  <span className="font-mono-fine text-xs tracking-[0.2em] uppercase text-[var(--pta-ink)]/60">Phone</span>
                  <span className="font-serif-display text-2xl text-[var(--pta-navy)] group-hover:text-[var(--pta-gold)]">+91 9560 029 986</span>
                </a>
                <a data-testid="cta-email" href="mailto:Pankaj.talan@MukMan.com" className="flex items-baseline justify-between py-4 group">
                  <span className="font-mono-fine text-xs tracking-[0.2em] uppercase text-[var(--pta-ink)]/60">Email</span>
                  <span className="font-serif-display text-lg md:text-xl text-[var(--pta-navy)] group-hover:text-[var(--pta-gold)]">Pankaj.talan@MukMan.com</span>
                </a>
                <a data-testid="cta-wa" href="https://wa.me/919560029986" target="_blank" rel="noreferrer" className="flex items-baseline justify-between py-4 group">
                  <span className="font-mono-fine text-xs tracking-[0.2em] uppercase text-[var(--pta-ink)]/60">WhatsApp</span>
                  <span className="font-serif-display text-2xl text-[var(--pta-navy)] group-hover:text-[var(--pta-gold)]">Chat now</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
